import axios from "axios";
import { ICellRendererParams } from "ag-grid-community";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Icon, Loader } from "rsuite";
import { FileService } from "../../../services/FileService";
import "./GridInlineThumbnailCell.scss";

// ---------------------------------------------------------------------------

const ThumbnailImage = ({
  id,
  onClick,
}: {
  id: string;
  onClick: () => void;
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoked = false;
    const url = FileService.getFileURL(id, "preview", "");

    axios
      .get(url, { responseType: "blob" })
      .then((res) => {
        if (!revoked) setBlobUrl(URL.createObjectURL(res.data));
      })
      .catch(() => {
        if (!revoked) setError(true);
      });

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) return null;

  if (!blobUrl) return <div className="grid-thumbnail-skeleton" />;

  return (
    <img
      src={blobUrl}
      alt=""
      className="grid-thumbnail-img"
      onClick={onClick}
    />
  );
};

// ---------------------------------------------------------------------------

const FullscreenPreview = ({
  ids,
  initialIndex,
  onClose,
}: {
  ids: string[];
  initialIndex: number;
  onClose: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    setBlobUrl(null);

    axios
      .get(FileService.getFileURL(ids[currentIndex], "original", ""), {
        responseType: "blob",
      })
      .then((res) => {
        if (!revoked) setBlobUrl(URL.createObjectURL(res.data));
      })
      .catch(() => {});

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        setCurrentIndex((prev) => (prev + 1) % ids.length);
      if (e.key === "ArrowLeft")
        setCurrentIndex((prev) => (prev - 1 + ids.length) % ids.length);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, ids.length]);

  return createPortal(
    <div className="GridInlineThumbnailCell">
      <div className="fullscreen-overlay" onClick={onClose}>
        <button
          className="fullscreen-nav fullscreen-nav-left"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => (prev - 1 + ids.length) % ids.length);
          }}
        >
          <Icon icon="angle-left" size="3x" />
        </button>

        <div
          className="fullscreen-content"
          onClick={(e) => e.stopPropagation()}
        >
          {blobUrl ? (
            <>
              <img
                src={blobUrl}
                alt=""
                className="fullscreen-image"
              />
              {ids.length > 1 && (
                <div className="fullscreen-caption">
                  {currentIndex + 1} / {ids.length}
                </div>
              )}
            </>
          ) : (
            <Loader size="lg" />
          )}
        </div>

        <button
          className="fullscreen-nav fullscreen-nav-right"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => (prev + 1) % ids.length);
          }}
        >
          <Icon icon="angle-right" size="3x" />
        </button>

        <button className="fullscreen-close" onClick={onClose}>
          <Icon icon="close" size="2x" />
        </button>
      </div>
    </div>,
    document.body,
  );
};

// ---------------------------------------------------------------------------

export const GridInlineThumbnailCell = (params: ICellRendererParams) => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);

  const value = params.value as string | null | undefined;
  if (!value) return null;

  const ids = value
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  if (ids.length === 0) return null;

  return (
    <div className="grid-thumbnail-strip">
      {fullscreenIndex !== null && (
        <FullscreenPreview
          ids={ids}
          initialIndex={fullscreenIndex}
          onClose={() => setFullscreenIndex(null)}
        />
      )}
      {ids.map((id, index) => (
        <ThumbnailImage
          key={id}
          id={id}
          onClick={() => setFullscreenIndex(index)}
        />
      ))}
    </div>
  );
};
